import {
  faUserTag,
  faMicrophone,
  faTags,
  faHeart,
  faSmile,
  faGlobe,
  faKey,
  faFileText,
  faClock,
  faCheckCircle,
  faExclamationCircle,
  faTimesCircle,
  faSpinner,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

/**
 * Get the FontAwesome icon for an enrichment type
 */
export const getEnrichmentIcon = (type: string | undefined): IconDefinition => {
  switch (type) {
    case "named_entity_recognition":
      return faUserTag;
    case "speech_to_text":
      return faMicrophone;
    case "classification":
      return faTags;
    case "sentiment_analysis":
      return faHeart;
    case "emotion_detection":
      return faSmile;
    case "language_detection":
      return faGlobe;
    case "keyword_extraction":
      return faKey;
    case "summarization":
      return faFileText;
    case "threat_detection":
      return faShieldAlt;
    default:
      return faUserTag;
  }
};

/**
 * Get the display label for an enrichment type
 */
export const getTypeLabel = (type: string | undefined): string => {
  if (!type) return "";

  const labels: Record<string, string> = {
    named_entity_recognition: "Extraction d'entités",
    speech_to_text: "Speech-to-Text",
    classification: "Classification",
    sentiment_analysis: "Analyse de sentiment",
    emotion_detection: "Détection d'émotions",
    language_detection: "Détection de langue",
    keyword_extraction: "Extraction de mots-clés",
    summarization: "Résumé automatique",
    workflow_n8n: "Workflow n8n",
    concept_extraction: "Extraction de concepts",
    logo_detection: "Détection de logos",
    comments_extraction: "Récupération commentaires",
    media_geolocation: "Géolocalisation média",
    threat_detection: "Détection de menaces",
  };
  return labels[type] || type;
};

/**
 * Get the FontAwesome icon for a status
 */
export const getStatusIcon = (status: string | undefined): IconDefinition => {
  switch (status) {
    case "active":
    case "completed":
      return faCheckCircle;
    case "inactive":
      return faTimesCircle;
    case "error":
      return faExclamationCircle;
    case "in_progress":
      return faSpinner;
    case "pending":
      return faClock;
    default:
      return faClock;
  }
};

/**
 * Get the color variant for a status
 */
export const getStatusColor = (
  status: string | undefined,
): "success" | "error" | "warning" | "info" | "default" => {
  switch (status) {
    case "active":
    case "completed":
      return "success";
    case "inactive":
      return "default";
    case "error":
      return "error";
    case "in_progress":
      return "info";
    case "pending":
      return "warning";
    default:
      return "warning";
  }
};

/**
 * Get the display text for a status
 */
export const getStatusText = (status: string | undefined): string => {
  if (!status) return "";

  switch (status) {
    case "active":
      return "Actif";
    case "inactive":
      return "Inactif";
    case "error":
      return "Erreur";
    case "completed":
      return "Terminé";
    case "in_progress":
      return "En cours";
    case "pending":
      return "En attente";
    default:
      return status;
  }
};

/**
 * Format a frequency in minutes to a human-readable French string
 */
export const formatFrequency = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}j`;
};

/**
 * Format a date string to French locale
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
